/**
 * Dark8-s Operator Dashboard
 *
 * Screens:
 *   A. Home / Command View    — workflow summary + governance banner
 *   B. Review Queue           — list concepts awaiting human review
 *   C. Concept Detail         — full detail + approve/reject/revision controls
 *   D. Draft Package          — prepare 8-second script draft (approved only)
 *   E. Manual Export          — generate export package (approved + draft only)
 *   F. Concept Intake         — submit new concept for classification
 *
 * Authority boundary:
 *   - This UI displays backend state; it does not create policy.
 *   - Rust classifier remains the classification authority.
 *   - Human operator is the sole review authority.
 *   - publish_allowed is always false. No publishing buttons exist.
 *   - No platform credentials, OAuth, schedulers, or upload integrations.
 */

import { useState, useEffect, useCallback } from "react";
import { Toaster, toast } from "sonner";
import {
    AlertTriangle,
    CheckCircle,
    XCircle,
    Clock,
    RefreshCw,
    ChevronRight,
    Shield,
    FileText,
    Send,
    Copy,
    Download,
    ArrowLeft,
    Cpu,
    Eye,
    PenLine,
} from "lucide-react";
import {
    getReviewQueue,
    reviewConcept,
    prepareDraft,
    exportConcept,
    ingestConcept,
    getConceptsByStatus,
} from "../lib/api";
import {
    AssetPackageScreen,
    AssetReviewScreen,
    ProductionExportScreen,
} from "./AssetSandboxScreens";
import { OperatorIdentityPanel } from "../components/OperatorIdentityPanel";

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const TIER_STYLES = {
    GREEN_PUBLIC_SAFE: "text-[#22c55e] border-[#22c55e]/30 bg-[#22c55e]/5",
    YELLOW_CAUTION: "text-[#ffb800] border-[#ffb800]/30 bg-[#ffb800]/5",
    ORANGE_RESTRICTED: "text-[#f97316] border-[#f97316]/30 bg-[#f97316]/5",
    RED_REJECT: "text-[#ff2a2a] border-[#ff2a2a]/30 bg-[#ff2a2a]/10",
    BLACK_HARD_BAN: "text-[#ff2a2a] border-[#ff2a2a] bg-[#ff2a2a]/20",
};

const STATUS_STYLES = {
    pending: "text-[#ffb800] bg-[#ffb800]/10 border-[#ffb800]/30",
    approved: "text-[#22c55e] bg-[#22c55e]/10 border-[#22c55e]/30",
    rejected: "text-[#ff2a2a] bg-[#ff2a2a]/10 border-[#ff2a2a]/30",
    needs_revision: "text-[#f97316] bg-[#f97316]/10 border-[#f97316]/30",
};

const TABS = [
    { id: "home", label: "Home" },
    { id: "queue", label: "Review Queue" },
    { id: "intake", label: "New Concept" },
];

const GOVERNANCE_BANNER =
    "MANUAL PUBLISHING ONLY — publish_allowed=false — Human operator review required before any distribution.";

// ─────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────

const GovernanceBanner = () => (
    <div
        data-testid="governance-banner"
        className="flex items-center gap-3 bg-[#ff2a2a]/10 border border-[#ff2a2a]/40 px-4 py-3 font-mono-d8 text-[11px] text-[#ff2a2a] uppercase tracking-widest"
    >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>{GOVERNANCE_BANNER}</span>
    </div>
);

const RustBoundaryBadge = () => (
    <div
        data-testid="rust-boundary-badge"
        className="inline-flex items-center gap-2 border border-[#ffb800]/30 bg-[#ffb800]/5 px-3 py-1.5 font-mono-d8 text-[10px] text-[#ffb800] uppercase tracking-widest"
    >
        <Cpu className="w-3 h-3" />
        Rust classifier authority — classification is read-only in this UI
    </div>
);

const TierBadge = ({ tier }) => {
    const cls = TIER_STYLES[tier] || "text-[#8a8a8a] border-[#262626] bg-transparent";
    return (
        <span className={`inline-block border px-2 py-0.5 font-mono-d8 text-[10px] uppercase tracking-widest ${cls}`}>
            {tier || "unknown"}
        </span>
    );
};

const StatusBadge = ({ status }) => {
    const cls = STATUS_STYLES[status] || "text-[#8a8a8a] border-[#262626] bg-transparent";
    return (
        <span className={`inline-block border px-2 py-0.5 font-mono-d8 text-[10px] uppercase tracking-widest ${cls}`}>
            {status || "unknown"}
        </span>
    );
};

const PublishBlockedMarker = () => (
    <span
        data-testid="publish-blocked-marker"
        className="inline-flex items-center gap-1 font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest"
    >
        <XCircle className="w-3 h-3" /> publish_allowed=false
    </span>
);

const SectionHeader = ({ label, children }) => (
    <section className="border-b border-[#262626] px-6 md:px-12 py-8 noise-bg">
        <div className="d8-label mb-2">/ Operator Dashboard / {label}</div>
        {children}
    </section>
);

const BackButton = ({ onClick }) => (
    <button
        data-testid="back-button"
        onClick={onClick}
        className="inline-flex items-center gap-2 font-mono-d8 text-xs text-[#8a8a8a] hover:text-[#f5f5f0] uppercase tracking-widest mb-6 transition-colors"
    >
        <ArrowLeft className="w-3 h-3" /> Back
    </button>
);

const ErrorBox = ({ msg }) =>
    msg ? (
        <div className="border border-[#ff2a2a]/40 bg-[#ff2a2a]/5 px-4 py-3 font-mono-d8 text-xs text-[#ff2a2a]">
            {msg}
        </div>
    ) : null;

// ─────────────────────────────────────────────────────────────
// Screen A: Home / Command View
// ─────────────────────────────────────────────────────────────

const HomeScreen = ({ onNavigate }) => {
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [pending, needsRevision, approved, rejected] = await Promise.all([
                getConceptsByStatus("pending"),
                getConceptsByStatus("needs_revision"),
                getConceptsByStatus("approved"),
                getConceptsByStatus("rejected"),
            ]);
            setCounts({
                pending: pending.length,
                needs_revision: needsRevision.length,
                approved: approved.length,
                rejected: rejected.length,
            });
        } catch {
            setCounts({ pending: 0, needs_revision: 0, approved: 0, rejected: 0 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const stats = [
        { label: "Pending Review", key: "pending", icon: Clock, color: "text-[#ffb800]" },
        { label: "Needs Revision", key: "needs_revision", icon: PenLine, color: "text-[#f97316]" },
        { label: "Approved", key: "approved", icon: CheckCircle, color: "text-[#22c55e]" },
        { label: "Rejected", key: "rejected", icon: XCircle, color: "text-[#ff2a2a]" },
    ];

    return (
        <div data-testid="home-screen">
            <SectionHeader label="Command View">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                    Operator<span className="text-[#ff2a2a]"> Command</span>
                </h1>
                <p className="mt-3 text-[#8a8a8a] max-w-2xl text-sm">
                    Governed concept workflow. All review authority belongs to the human operator.
                    No publishing authority is created here.
                </p>
            </SectionHeader>

            <div className="px-6 md:px-12 py-8 space-y-8">
                <GovernanceBanner />
                <OperatorIdentityPanel />
                <RustBoundaryBadge />

                {/* Workflow summary */}
                <div>
                    <div className="d8-label mb-4">Workflow Status</div>
                    {loading ? (
                        <div className="font-mono-d8 text-xs text-[#8a8a8a]">Loading…</div>
                    ) : (
                        <div
                            data-testid="workflow-summary"
                            className="grid grid-cols-2 md:grid-cols-4 gap-3"
                        >
                            {stats.map(({ label, key, icon: Icon, color }) => (
                                <div
                                    key={key}
                                    className="border border-[#262626] bg-[#0a0a0a] p-4 space-y-2"
                                >
                                    <div className="flex items-center gap-2">
                                        <Icon className={`w-4 h-4 ${color}`} />
                                        <span className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest">
                                            {label}
                                        </span>
                                    </div>
                                    <div className={`font-heading font-black text-3xl ${color}`}>
                                        {counts?.[key] ?? "—"}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Quick actions */}
                <div>
                    <div className="d8-label mb-4">Quick Actions</div>
                    <div className="flex flex-wrap gap-3">
                        <button
                            data-testid="go-to-queue"
                            onClick={() => onNavigate("queue")}
                            className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#262626] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-widest px-5 py-3 transition-colors"
                        >
                            <Eye className="w-4 h-4" /> Review Queue
                            <ChevronRight className="w-3 h-3 text-[#8a8a8a]" />
                        </button>
                        <button
                            data-testid="go-to-intake"
                            onClick={() => onNavigate("intake")}
                            className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#262626] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-widest px-5 py-3 transition-colors"
                        >
                            <Send className="w-4 h-4" /> New Concept
                            <ChevronRight className="w-3 h-3 text-[#8a8a8a]" />
                        </button>
                        <button
                            data-testid="refresh-summary"
                            onClick={load}
                            className="inline-flex items-center gap-2 border border-[#262626] text-[#8a8a8a] hover:text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-widest px-5 py-3 transition-colors"
                        >
                            <RefreshCw className="w-3 h-3" /> Refresh
                        </button>
                    </div>
                </div>

                {/* No-publishing warning */}
                <div
                    data-testid="no-publishing-warning"
                    className="border border-[#262626] bg-[#0a0a0a] p-4 font-mono-d8 text-[11px] text-[#8a8a8a] space-y-1"
                >
                    <div className="flex items-center gap-2 text-[#ff2a2a] font-bold uppercase tracking-widest">
                        <Shield className="w-3 h-3" /> No Publishing Authority
                    </div>
                    <p>
                        This dashboard does not grant publishing authority. No platform
                        credentials, OAuth tokens, schedulers, or upload integrations exist.
                        All content distribution requires manual operator action outside this system.
                    </p>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// Screen B: Review Queue
// ─────────────────────────────────────────────────────────────

const ReviewQueueScreen = ({ onSelectConcept }) => {
    const [concepts, setConcepts] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getReviewQueue(filter || undefined);
            setConcepts(data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load review queue.");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const filters = [
        { value: "", label: "All Queue Items" },
        { value: "pending", label: "Pending" },
        { value: "needs_revision", label: "Needs Revision" },
    ];

    return (
        <div data-testid="review-queue-screen">
            <SectionHeader label="Review Queue">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-4xl leading-none">
                    Review <span className="text-[#ff2a2a]">Queue</span>
                </h1>
                <p className="mt-3 text-[#8a8a8a] max-w-2xl text-sm">
                    Concepts awaiting human operator review. Classification is read-only — set by the Rust kernel.
                </p>
            </SectionHeader>

            <div className="px-6 md:px-12 py-8 space-y-6">
                <GovernanceBanner />

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2">
                    {filters.map((f) => (
                        <button
                            key={f.value}
                            data-testid={`queue-filter-${f.value || "all"}`}
                            onClick={() => setFilter(f.value)}
                            className={`font-mono-d8 text-[10px] uppercase tracking-widest px-4 py-2 border transition-colors ${
                                filter === f.value
                                    ? "border-[#ff2a2a] text-[#ff2a2a] bg-[#ff2a2a]/5"
                                    : "border-[#262626] text-[#8a8a8a] hover:text-[#f5f5f0]"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    <button
                        data-testid="queue-refresh"
                        onClick={load}
                        className="ml-auto inline-flex items-center gap-1 font-mono-d8 text-[10px] text-[#8a8a8a] hover:text-[#f5f5f0] uppercase tracking-widest transition-colors"
                    >
                        <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                </div>

                <ErrorBox msg={error} />

                {loading ? (
                    <div className="font-mono-d8 text-xs text-[#8a8a8a]">Loading queue…</div>
                ) : concepts.length === 0 ? (
                    <div
                        data-testid="queue-empty"
                        className="border border-[#262626] bg-[#0a0a0a] p-8 text-center font-mono-d8 text-xs text-[#8a8a8a]"
                    >
                        No concepts in queue for this filter.
                    </div>
                ) : (
                    <div data-testid="queue-list" className="space-y-2">
                        {concepts.map((c) => (
                            <ConceptRow
                                key={c.concept_id}
                                concept={c}
                                onClick={() => onSelectConcept(c)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const ConceptRow = ({ concept: c, onClick }) => {
    const isBlocked =
        c.classification === "RED_REJECT" || c.classification === "BLACK_HARD_BAN";

    return (
        <button
            data-testid={`concept-row-${c.concept_id}`}
            onClick={onClick}
            className={`w-full text-left border p-4 transition-colors hover:bg-[#0f0f0f] ${
                isBlocked
                    ? "border-[#ff2a2a]/40 bg-[#ff2a2a]/5"
                    : "border-[#262626] bg-[#0a0a0a]"
            }`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading font-bold text-[#f5f5f0] text-sm truncate">
                            {c.title}
                        </span>
                        {isBlocked && (
                            <span className="font-mono-d8 text-[9px] text-[#ff2a2a] uppercase tracking-widest border border-[#ff2a2a]/40 px-1.5 py-0.5">
                                BLOCKED
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <TierBadge tier={c.classification} />
                        <StatusBadge status={c.review_status} />
                        {c.human_review_required && (
                            <span className="font-mono-d8 text-[9px] text-[#ffb800] uppercase tracking-widest">
                                ⚠ review required
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3 font-mono-d8 text-[10px] text-[#8a8a8a]">
                        <span>ch: {c.intended_channel}</span>
                        <span>{new Date(c.created_at).toLocaleDateString()}</span>
                        <PublishBlockedMarker />
                    </div>
                    {c.classification_reason_codes?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {c.classification_reason_codes.map((rc) => (
                                <span
                                    key={rc}
                                    className="font-mono-d8 text-[9px] text-[#8a8a8a] border border-[#262626] px-1.5 py-0.5"
                                >
                                    {rc}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <ChevronRight className="w-4 h-4 text-[#8a8a8a] shrink-0 mt-1" />
            </div>
        </button>
    );
};

// ─────────────────────────────────────────────────────────────
// Screen C: Concept Detail + Review Controls
// ─────────────────────────────────────────────────────────────

const ConceptDetailScreen = ({ concept: initial, onBack, onNavigateToDraft }) => {
    const [concept, setConcept] = useState(initial);
    const [reviewStatus, setReviewStatus] = useState("");
    const [operatorNote, setOperatorNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const canReview = concept.review_status === "pending" || concept.review_status === "needs_revision";
    const isApproved = concept.review_status === "approved";
    const isRejected = concept.review_status === "rejected";

    const allowedTransitions = {
        pending: ["approved", "rejected", "needs_revision"],
        needs_revision: ["pending", "rejected"],
        approved: [],
        rejected: [],
    };
    const transitions = allowedTransitions[concept.review_status] || [];

    const submitReview = async () => {
        if (!reviewStatus) { setError("Select a review decision."); return; }
        if (!operatorNote.trim()) { setError("Operator note is required."); return; }
        setError("");
        setSubmitting(true);
        try {
            const updated = await reviewConcept(concept.concept_id, reviewStatus, operatorNote.trim());
            setConcept(updated);
            setReviewStatus("");
            setOperatorNote("");
            toast.success(`Review submitted: ${updated.review_status}`);
        } catch (e) {
            const msg = e?.response?.data?.detail;
            setError(typeof msg === "string" ? msg : JSON.stringify(msg) || "Review failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="concept-detail-screen">
            <SectionHeader label="Concept Detail">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-2xl md:text-4xl leading-none">
                    {concept.title}
                </h1>
            </SectionHeader>

            <div className="px-6 md:px-12 py-8 space-y-8 max-w-4xl">
                <BackButton onClick={onBack} />
                <GovernanceBanner />

                {/* Classification (read-only) */}
                <div className="border border-[#262626] bg-[#0a0a0a] p-5 space-y-4">
                    <div className="flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-[#ffb800]" />
                        <span className="font-mono-d8 text-[10px] text-[#ffb800] uppercase tracking-widest">
                            Rust Classifier Result — Read Only
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-3 items-center">
                        <TierBadge tier={concept.classification} />
                        <StatusBadge status={concept.review_status} />
                        <PublishBlockedMarker />
                    </div>
                    {concept.classification_reason_codes?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {concept.classification_reason_codes.map((rc) => (
                                <span key={rc} className="font-mono-d8 text-[9px] text-[#8a8a8a] border border-[#262626] px-1.5 py-0.5">
                                    {rc}
                                </span>
                            ))}
                        </div>
                    )}
                    {concept.classification_explanation && (
                        <p className="font-mono-d8 text-xs text-[#8a8a8a]">
                            {concept.classification_explanation}
                        </p>
                    )}
                    {concept.classification_operator_action && (
                        <p className="font-mono-d8 text-[10px] text-[#ffb800]">
                            Operator action: {concept.classification_operator_action}
                        </p>
                    )}
                    {concept.input_title_hash && (
                        <p className="font-mono-d8 text-[9px] text-[#8a8a8a]">
                            Input hash: {concept.input_title_hash}
                        </p>
                    )}
                </div>

                {/* Concept fields */}
                <div className="space-y-4">
                    {[
                        ["Script Text", concept.script_text],
                        ["Target Audience", concept.target_audience],
                        ["Intended Channel", concept.intended_channel],
                        ["Distribution Intent", concept.public_or_subscription_intent],
                    ].map(([label, val]) => (
                        <div key={label}>
                            <div className="d8-label mb-1">{label}</div>
                            <div className="bg-[#0a0a0a] border border-[#262626] p-3 font-mono-d8 text-xs text-[#f5f5f0] whitespace-pre-wrap">
                                {val || "—"}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Operator notes history */}
                {concept.operator_notes?.length > 0 && (
                    <div>
                        <div className="d8-label mb-2">Operator Notes History</div>
                        <div className="space-y-1">
                            {concept.operator_notes.map((n, i) => (
                                <div key={i} className="bg-[#0a0a0a] border border-[#262626] p-2 font-mono-d8 text-[10px] text-[#8a8a8a]">
                                    {n}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Review controls */}
                {canReview && (
                    <div
                        data-testid="review-controls"
                        className="border border-[#262626] bg-[#0a0a0a] p-5 space-y-4"
                    >
                        <div className="d8-label">Review Decision</div>
                        <div className="flex flex-wrap gap-2">
                            {transitions.map((t) => (
                                <button
                                    key={t}
                                    data-testid={`review-btn-${t}`}
                                    onClick={() => setReviewStatus(t)}
                                    className={`font-mono-d8 text-[10px] uppercase tracking-widest px-4 py-2 border transition-colors ${
                                        reviewStatus === t
                                            ? t === "approved"
                                                ? "border-[#22c55e] text-[#22c55e] bg-[#22c55e]/10"
                                                : t === "rejected"
                                                ? "border-[#ff2a2a] text-[#ff2a2a] bg-[#ff2a2a]/10"
                                                : "border-[#f97316] text-[#f97316] bg-[#f97316]/10"
                                            : "border-[#262626] text-[#8a8a8a] hover:text-[#f5f5f0]"
                                    }`}
                                >
                                    {t.replace("_", " ")}
                                </button>
                            ))}
                        </div>
                        <div>
                            <div className="d8-label mb-1">Operator Note (required)</div>
                            <textarea
                                data-testid="operator-note-input"
                                value={operatorNote}
                                onChange={(e) => setOperatorNote(e.target.value)}
                                maxLength={1000}
                                rows={3}
                                placeholder="Enter your review note…"
                                className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3 resize-none focus:outline-none focus:border-[#ff2a2a]"
                            />
                        </div>
                        <ErrorBox msg={error} />
                        <button
                            data-testid="submit-review"
                            onClick={submitReview}
                            disabled={submitting}
                            className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] disabled:opacity-50 text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Submit Review
                        </button>
                    </div>
                )}

                {/* Terminal state notice */}
                {(isApproved || isRejected) && (
                    <div className={`border p-4 font-mono-d8 text-xs ${isApproved ? "border-[#22c55e]/30 text-[#22c55e]" : "border-[#ff2a2a]/30 text-[#ff2a2a]"}`}>
                        {isApproved
                            ? "Concept is approved. No further review transitions are allowed."
                            : "Concept is rejected. No further review transitions are allowed."}
                    </div>
                )}

                {/* Draft navigation (approved only) */}
                {isApproved && (
                    <button
                        data-testid="go-to-draft"
                        onClick={() => onNavigateToDraft(concept)}
                        className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#262626] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-widest px-5 py-3 transition-colors"
                    >
                        <FileText className="w-4 h-4" /> Prepare Draft Package
                        <ChevronRight className="w-3 h-3 text-[#8a8a8a]" />
                    </button>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// Screen D: Draft Package
// ─────────────────────────────────────────────────────────────

const DraftScreen = ({ concept, onBack, onNavigateToExport }) => {
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isApproved = concept.review_status === "approved";

    const prepare = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await prepareDraft(concept.concept_id);
            setDraft(data);
            toast.success("Draft prepared.");
        } catch (e) {
            const msg = e?.response?.data?.detail;
            setError(typeof msg === "string" ? msg : JSON.stringify(msg) || "Draft preparation failed.");
        } finally {
            setLoading(false);
        }
    };

    const draftFields = draft
        ? [
              ["Hook", draft.hook],
              ["Setup", draft.setup],
              ["Punchline / Twist", draft.punchline_twist],
              ["Visual Cue", draft.visual_cue],
              ["Platform Notes", draft.platform_notes],
              ["Risk Notes", draft.risk_notes],
              ["Classification Reference", draft.classification_reference],
          ]
        : [];

    return (
        <div data-testid="draft-screen">
            <SectionHeader label="Draft Package">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-2xl md:text-4xl leading-none">
                    8-Second <span className="text-[#ff2a2a]">Draft</span>
                </h1>
                <p className="mt-2 text-[#8a8a8a] text-sm">{concept.title}</p>
            </SectionHeader>

            <div className="px-6 md:px-12 py-8 space-y-8 max-w-4xl">
                <BackButton onClick={onBack} />
                <GovernanceBanner />

                {!isApproved && (
                    <div
                        data-testid="draft-blocked"
                        className="border border-[#ff2a2a]/30 bg-[#ff2a2a]/5 p-5 font-mono-d8 text-xs text-[#ff2a2a] space-y-2"
                    >
                        <div className="font-bold uppercase tracking-widest">Draft Unavailable</div>
                        <p>
                            Draft preparation requires <strong>approved</strong> status. Current status:{" "}
                            <StatusBadge status={concept.review_status} />
                        </p>
                        <p>Return to the concept detail and complete human review first.</p>
                    </div>
                )}

                {isApproved && !draft && (
                    <div className="space-y-4">
                        <div className="border border-[#262626] bg-[#0a0a0a] p-4 font-mono-d8 text-xs text-[#8a8a8a]">
                            Concept is approved. You may prepare the 8-second script draft.
                            This does not grant publishing authority.
                        </div>
                        <ErrorBox msg={error} />
                        <button
                            data-testid="prepare-draft-btn"
                            onClick={prepare}
                            disabled={loading}
                            className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] disabled:opacity-50 text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            Prepare 8-Second Draft
                        </button>
                    </div>
                )}

                {draft && (
                    <div data-testid="draft-result" className="space-y-6">
                        <div className="flex flex-wrap items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                            <span className="font-mono-d8 text-xs text-[#22c55e]">Draft prepared</span>
                            <PublishBlockedMarker />
                            {draft.human_review_note && (
                                <span className="font-mono-d8 text-[10px] text-[#ffb800]">
                                    {draft.human_review_note}
                                </span>
                            )}
                        </div>

                        {/* Script timeline */}
                        {draft.script?.length > 0 && (
                            <div>
                                <div className="d8-label mb-2">Script Timeline</div>
                                <div className="space-y-1">
                                    {draft.script.map((block, i) => (
                                        <div key={i} className="flex gap-3 bg-[#0a0a0a] border border-[#262626] p-3">
                                            <span className="font-mono-d8 text-[10px] text-[#ff2a2a] shrink-0 w-20">
                                                {block.start}–{block.end}
                                            </span>
                                            <span className="font-mono-d8 text-[10px] text-[#ffb800] shrink-0 w-24">
                                                {block.label}
                                            </span>
                                            <span className="font-mono-d8 text-xs text-[#f5f5f0]">
                                                {block.text}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Draft fields */}
                        {draftFields.map(([label, val]) =>
                            val ? (
                                <div key={label}>
                                    <div className="d8-label mb-1">{label}</div>
                                    <div className="bg-[#0a0a0a] border border-[#262626] p-3 font-mono-d8 text-xs text-[#f5f5f0] whitespace-pre-wrap">
                                        {val}
                                    </div>
                                </div>
                            ) : null
                        )}

                        <button
                            data-testid="go-to-export"
                            onClick={() => onNavigateToExport(concept)}
                            className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#262626] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-widest px-5 py-3 transition-colors"
                        >
                            <Download className="w-4 h-4" /> Generate Export Package
                            <ChevronRight className="w-3 h-3 text-[#8a8a8a]" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// Screen E: Manual Export
// ─────────────────────────────────────────────────────────────

const ExportScreen = ({ concept, onBack, onNavigateToAssetPackage }) => {
    const [exportPkg, setExportPkg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    const generate = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await exportConcept(concept.concept_id);
            setExportPkg(data);
            toast.success("Export package generated.");
        } catch (e) {
            const msg = e?.response?.data?.detail;
            setError(typeof msg === "string" ? msg : JSON.stringify(msg) || "Export failed.");
        } finally {
            setLoading(false);
        }
    };

    const copyJson = () => {
        if (!exportPkg) return;
        navigator.clipboard?.writeText(JSON.stringify(exportPkg, null, 2)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div data-testid="export-screen">
            <SectionHeader label="Manual Export">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-2xl md:text-4xl leading-none">
                    Manual <span className="text-[#ff2a2a]">Export</span>
                </h1>
                <p className="mt-2 text-[#8a8a8a] text-sm">{concept.title}</p>
            </SectionHeader>

            <div className="px-6 md:px-12 py-8 space-y-8 max-w-4xl">
                <BackButton onClick={onBack} />

                {/* Critical warning */}
                <div
                    data-testid="manual-publishing-warning"
                    className="border-2 border-[#ff2a2a] bg-[#ff2a2a]/10 p-5 space-y-2"
                >
                    <div className="flex items-center gap-2 font-mono-d8 text-sm text-[#ff2a2a] font-bold uppercase tracking-widest">
                        <AlertTriangle className="w-5 h-5" /> Critical
                    </div>
                    <p className="font-mono-d8 text-xs text-[#ff2a2a]">
                        MANUAL PUBLISHING ONLY. No autonomous or automated release authority is
                        granted by this system. publish_allowed=false. This package must be
                        manually copy-pasted by a human operator. No platform buttons exist.
                        No upload integrations exist. No scheduler exists.
                    </p>
                </div>

                {!exportPkg && (
                    <div className="space-y-4">
                        <div className="border border-[#262626] bg-[#0a0a0a] p-4 font-mono-d8 text-xs text-[#8a8a8a]">
                            Generate the manual export package for human operator use.
                            This package contains the script draft and classification reference only.
                            It does not grant publishing authority.
                        </div>
                        <ErrorBox msg={error} />
                        <button
                            data-testid="generate-export-btn"
                            onClick={generate}
                            disabled={loading}
                            className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] disabled:opacity-50 text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            Generate Export Package
                        </button>
                    </div>
                )}

                {exportPkg && (
                    <div data-testid="export-result" className="space-y-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                            <span className="font-mono-d8 text-xs text-[#22c55e]">Export package ready</span>
                            <PublishBlockedMarker />
                        </div>

                        {exportPkg.manual_publishing_header && (
                            <div className="border border-[#ff2a2a]/30 bg-[#ff2a2a]/5 p-3 font-mono-d8 text-[10px] text-[#ff2a2a]">
                                {exportPkg.manual_publishing_header}
                            </div>
                        )}

                        <div className="flex gap-2">
                            <button
                                data-testid="copy-export-btn"
                                onClick={copyJson}
                                className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#ff2a2a] text-[#8a8a8a] hover:text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-widest px-4 py-2 transition-colors"
                            >
                                <Copy className="w-3 h-3" />
                                {copied ? "Copied!" : "Copy JSON"}
                            </button>
                        </div>

                        <pre
                            data-testid="export-json"
                            className="bg-[#0a0a0a] border border-[#262626] p-4 font-mono-d8 text-[11px] text-[#f5f5f0] overflow-x-auto max-h-[500px] overflow-y-auto"
                        >
                            {JSON.stringify(exportPkg, null, 2)}
                        </pre>

                        {/* Phase 18: Navigate to Asset Package */}
                        {onNavigateToAssetPackage && (
                            <button
                                data-testid="go-to-asset-package-btn"
                                onClick={() => onNavigateToAssetPackage(concept)}
                                className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                            >
                                <FileText className="w-4 h-4" /> Prepare Asset Package
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// Screen F: Concept Intake Form
// ─────────────────────────────────────────────────────────────

const CHANNELS = [
    "youtube_shorts", "tiktok", "instagram_reels", "facebook_reels", "x_shorts", "internal_test",
];

const IntakeScreen = ({ onConceptCreated }) => {
    const [form, setForm] = useState({
        title: "",
        script_text: "",
        target_audience: "general",
        intended_channel: "youtube_shorts",
        public_or_subscription_intent: "public",
    });
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async () => {
        if (!form.title.trim() || !form.script_text.trim()) {
            setError("Title and script text are required.");
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const data = await ingestConcept(form);
            setResult(data);
            toast.success(`Concept ingested. Classification: ${data.classification}`);
        } catch (e) {
            const msg = e?.response?.data?.detail;
            setError(typeof msg === "string" ? msg : JSON.stringify(msg) || "Intake failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setResult(null);
        setForm({
            title: "",
            script_text: "",
            target_audience: "general",
            intended_channel: "youtube_shorts",
            public_or_subscription_intent: "public",
        });
        setError("");
    };

    return (
        <div data-testid="intake-screen">
            <SectionHeader label="New Concept">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-4xl leading-none">
                    Concept <span className="text-[#ff2a2a]">Intake</span>
                </h1>
                <p className="mt-3 text-[#8a8a8a] max-w-2xl text-sm">
                    Submit a concept for Rust classification and human review. No publishing authority is created.
                </p>
            </SectionHeader>

            <div className="px-6 md:px-12 py-8 space-y-8 max-w-2xl">
                <GovernanceBanner />

                {result ? (
                    <div data-testid="intake-result" className="space-y-5">
                        <div className="border border-[#22c55e]/30 bg-[#22c55e]/5 p-5 space-y-3">
                            <div className="flex items-center gap-2 font-mono-d8 text-xs text-[#22c55e]">
                                <CheckCircle className="w-4 h-4" /> Concept ingested and classified
                            </div>
                            <div className="flex flex-wrap gap-2 items-center">
                                <TierBadge tier={result.classification} />
                                <StatusBadge status={result.review_status} />
                                <PublishBlockedMarker />
                            </div>
                            {result.classification_explanation && (
                                <p className="font-mono-d8 text-xs text-[#8a8a8a]">
                                    {result.classification_explanation}
                                </p>
                            )}
                            {result.classification_operator_action && (
                                <p className="font-mono-d8 text-[10px] text-[#ffb800]">
                                    {result.classification_operator_action}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <button
                                data-testid="view-in-queue"
                                onClick={() => onConceptCreated(result)}
                                className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-5 py-3 transition-colors"
                            >
                                <Eye className="w-4 h-4" /> View in Review Queue
                            </button>
                            <button
                                data-testid="intake-reset"
                                onClick={reset}
                                className="inline-flex items-center gap-2 border border-[#262626] text-[#8a8a8a] hover:text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-widest px-5 py-3 transition-colors"
                            >
                                Submit Another
                            </button>
                        </div>
                    </div>
                ) : (
                    <div data-testid="intake-form" className="space-y-5">
                        <div>
                            <div className="d8-label mb-1">Title *</div>
                            <input
                                data-testid="intake-title"
                                type="text"
                                value={form.title}
                                onChange={set("title")}
                                maxLength={200}
                                placeholder="Concept title…"
                                className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3 focus:outline-none focus:border-[#ff2a2a]"
                            />
                        </div>
                        <div>
                            <div className="d8-label mb-1">Script Text *</div>
                            <textarea
                                data-testid="intake-script"
                                value={form.script_text}
                                onChange={set("script_text")}
                                maxLength={4000}
                                rows={5}
                                placeholder="The 8-second concept script…"
                                className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3 resize-none focus:outline-none focus:border-[#ff2a2a]"
                            />
                        </div>
                        <div>
                            <div className="d8-label mb-1">Target Audience</div>
                            <input
                                data-testid="intake-audience"
                                type="text"
                                value={form.target_audience}
                                onChange={set("target_audience")}
                                maxLength={200}
                                className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3 focus:outline-none focus:border-[#ff2a2a]"
                            />
                        </div>
                        <div>
                            <div className="d8-label mb-1">Intended Channel</div>
                            <select
                                data-testid="intake-channel"
                                value={form.intended_channel}
                                onChange={set("intended_channel")}
                                className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3 focus:outline-none focus:border-[#ff2a2a]"
                            >
                                {CHANNELS.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <div className="d8-label mb-1">Distribution Intent</div>
                            <select
                                data-testid="intake-intent"
                                value={form.public_or_subscription_intent}
                                onChange={set("public_or_subscription_intent")}
                                className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3 focus:outline-none focus:border-[#ff2a2a]"
                            >
                                <option value="public">Public</option>
                                <option value="subscription">Subscription</option>
                                <option value="unknown">Unknown</option>
                            </select>
                        </div>
                        <ErrorBox msg={error} />
                        <button
                            data-testid="intake-submit"
                            onClick={submit}
                            disabled={submitting}
                            className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] disabled:opacity-50 text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Submit for Classification
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// Top-level tab navigation
// ─────────────────────────────────────────────────────────────

const TabBar = ({ active, onChange }) => (
    <div className="border-b border-[#262626] px-6 md:px-12 flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
            <button
                key={t.id}
                data-testid={`dash-tab-${t.id}`}
                onClick={() => onChange(t.id)}
                className={`font-mono-d8 text-[10px] uppercase tracking-widest px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    active === t.id
                        ? "border-[#ff2a2a] text-[#f5f5f0]"
                        : "border-transparent text-[#8a8a8a] hover:text-[#f5f5f0]"
                }`}
            >
                {t.label}
            </button>
        ))}
    </div>
);

// ─────────────────────────────────────────────────────────────
// Page root
// ─────────────────────────────────────────────────────────────

export const OperatorDashboard = () => {
    const [tab, setTab] = useState("home");
    const [selectedConcept, setSelectedConcept] = useState(null);
    const [screen, setScreen] = useState("tab"); // "tab" | "detail" | "draft" | "export" | "asset-package" | "asset-review" | "production-export"
    const [assetPkg, setAssetPkg] = useState(null);

    const goToDetail = (concept) => {
        setSelectedConcept(concept);
        setScreen("detail");
    };

    const goToDraft = (concept) => {
        setSelectedConcept(concept);
        setScreen("draft");
    };

    const goToExport = (concept) => {
        setSelectedConcept(concept);
        setScreen("export");
    };

    const goToAssetPackage = (concept) => {
        setSelectedConcept(concept);
        setScreen("asset-package");
    };

    const goToAssetReview = (concept, pkg) => {
        setSelectedConcept(concept);
        setAssetPkg(pkg);
        setScreen("asset-review");
    };

    const goToProductionExport = (concept) => {
        setSelectedConcept(concept);
        setScreen("production-export");
    };

    const goBack = () => {
        if (screen === "production-export") { setScreen("asset-review"); return; }
        if (screen === "asset-review") { setScreen("asset-package"); return; }
        if (screen === "asset-package") { setScreen("export"); return; }
        if (screen === "export") { setScreen("draft"); return; }
        if (screen === "draft") { setScreen("detail"); return; }
        setScreen("tab");
        setSelectedConcept(null);
    };

    const handleConceptCreated = (concept) => {
        setSelectedConcept(concept);
        setTab("queue");
        setScreen("tab");
    };

    return (
        <div data-testid="operator-dashboard" className="min-h-screen">
            <Toaster theme="dark" position="top-right" />

            {screen === "tab" && (
                <>
                    <TabBar active={tab} onChange={setTab} />
                    {tab === "home" && <HomeScreen onNavigate={setTab} />}
                    {tab === "queue" && <ReviewQueueScreen onSelectConcept={goToDetail} />}
                    {tab === "intake" && <IntakeScreen onConceptCreated={handleConceptCreated} />}
                </>
            )}

            {screen === "detail" && selectedConcept && (
                <ConceptDetailScreen
                    concept={selectedConcept}
                    onBack={goBack}
                    onNavigateToDraft={goToDraft}
                />
            )}

            {screen === "draft" && selectedConcept && (
                <DraftScreen
                    concept={selectedConcept}
                    onBack={goBack}
                    onNavigateToExport={goToExport}
                />
            )}

            {screen === "export" && selectedConcept && (
                <ExportScreen
                    concept={selectedConcept}
                    onBack={goBack}
                    onNavigateToAssetPackage={goToAssetPackage}
                />
            )}

            {screen === "asset-package" && selectedConcept && (
                <AssetPackageScreen
                    concept={selectedConcept}
                    onBack={goBack}
                    onNavigateToAssetReview={goToAssetReview}
                />
            )}

            {screen === "asset-review" && selectedConcept && (
                <AssetReviewScreen
                    concept={selectedConcept}
                    onBack={goBack}
                    onNavigateToProductionExport={goToProductionExport}
                />
            )}

            {screen === "production-export" && selectedConcept && (
                <ProductionExportScreen
                    concept={selectedConcept}
                    onBack={goBack}
                />
            )}
        </div>
    );
};

export default OperatorDashboard;
